/**
 * Editor com a Mia (Fase 4)
 * =========================
 * Widget de chat conversacional pra editar posts gpt_image.
 * A Mia (Azure gpt-4.1 + tool calling) conversa e SÓ dispara edição quando o
 * usuário pede de fato — a geração da imagem roda em background com polling.
 *
 *   ESQUERDA  → imagem em edição (grande)  |  EMPILHA no mobile
 *   DIREITA   → chat com a Mia
 */
import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Send, RefreshCw, Palette, Image as ImageIcon,
  Type, Loader2, Sparkles, AlertCircle, Download,
} from 'lucide-react';
import { contentService } from '../../services/ContentService';
import { useBrandIdentity } from '../../contexts/BrandIdentityContext';

const QUICK_CHIPS = [
  { id: 'colors', icon: Palette, label: 'Mudar cores', prompt: 'Deixa as cores da minha marca mais destacadas no post.' },
  { id: 'bg', icon: ImageIcon, label: 'Mudar fundo', prompt: 'Troca o fundo por um cenário mais sofisticado e relacionado ao tema.' },
  { id: 'title', icon: Type, label: 'Título maior', prompt: 'Deixa o título maior e com mais destaque.' },
  { id: 'redo', icon: RefreshCw, label: 'Mais clean', prompt: 'Refaz num estilo mais clean e minimalista.' },
];

const POLL_INTERVAL_MS = 2500;
const EDITING_MSG_INTERVAL_MS = 4500;
const EDITING_MESSAGES = [
  'Entendendo seu pedido… 🤔',
  'Buscando as melhores referências visuais… 🎨',
  'Ajustando cores e composição… 🖌️',
  'Capricho nos detalhes… ✨',
  'Deixando com a cara da sua marca… 💚',
  'Renderizando a nova versão… 🖼️',
  'Quase lá, dando os retoques finais… 🪄',
];

const GREETING = 'Oi! Eu sou a Mia 👋 Me conta o que você quer ajustar nesse post que eu faço pra você ✨';

// Fundo xadrez pra logos/imagens transparentes ficarem visíveis no preview
const CHECKER = {
  backgroundImage:
    'linear-gradient(45deg,#d1d5db 25%,transparent 25%),linear-gradient(-45deg,#d1d5db 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#d1d5db 75%),linear-gradient(-45deg,transparent 75%,#d1d5db 75%)',
  backgroundSize: '10px 10px',
  backgroundPosition: '0 0,0 5px,5px -5px,-5px 0',
  backgroundColor: '#f3f4f6',
};

const MIA_AVATAR_URL = 'https://hellosocialstore.blob.core.windows.net/chatfiles/faaa5e2a-4d73-43e9-be9b-10cce7aff5fe/20260512_172631_d687545e_ChatGPT%20Image%2012%20de%20mai.%20de%202026%2C%2014_25_16.png';

// Markdown leve e seguro (sem dependência / sem dangerouslySetInnerHTML).
// Suporta: **negrito**, *itálico*/_itálico_, `código`, quebras de linha e bullets (- / *).
const _inlineMd = (text, kp) => {
  const nodes = [];
  const re = /(\*\*([^*]+)\*\*|\*([^*]+)\*|_([^_]+)_|`([^`]+)`)/g;
  let last = 0, m, i = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    if (m[2] !== undefined) nodes.push(<strong key={`${kp}-b${i}`}>{m[2]}</strong>);
    else if (m[3] !== undefined) nodes.push(<em key={`${kp}-i${i}`}>{m[3]}</em>);
    else if (m[4] !== undefined) nodes.push(<em key={`${kp}-i${i}`}>{m[4]}</em>);
    else if (m[5] !== undefined) nodes.push(<code key={`${kp}-c${i}`} className="px-1 py-0.5 rounded bg-black/10 text-[0.85em] font-mono">{m[5]}</code>);
    last = re.lastIndex; i++;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const LightMarkdown = ({ text }) => {
  const lines = (text || '').split('\n');
  return (
    <>
      {lines.map((ln, idx) => {
        const b = /^\s*[-*]\s+(.*)/.exec(ln);
        if (b) {
          return (
            <div key={idx} className="flex gap-1.5">
              <span className="opacity-70">•</span>
              <span>{_inlineMd(b[1], `l${idx}`)}</span>
            </div>
          );
        }
        return <div key={idx}>{ln ? _inlineMd(ln, `l${idx}`) : ' '}</div>;
      })}
    </>
  );
};

// Avatar da Mia (foto real; cai num gradiente se a imagem falhar)
const MiaAvatar = ({ size = 'w-9 h-9' }) => {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className={`${size} rounded-full bg-gradient-to-br from-amber-400 via-pink-400 to-purple-500 flex items-center justify-center text-white font-bold shadow-sm shrink-0 select-none`}>M</div>
    );
  }
  return (
    <img src={MIA_AVATAR_URL} alt="Mia" onError={() => setErr(true)}
      className={`${size} rounded-full object-cover shadow-sm shrink-0 select-none bg-amber-100`} />
  );
};

const GptImageChatEditor = () => {
  const { batchId, contentId } = useParams();
  const { brandIdentity, logoUrl } = useBrandIdentity();

  const [content, setContent] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);   // aguardando resposta da Mia
  const [isProcessing, setIsProcessing] = useState(false); // gerando imagem (background)
  const [error, setError] = useState(null);
  const [editsUsed, setEditsUsed] = useState(0);
  const [editsLimit, setEditsLimit] = useState(3);
  const [attachments, setAttachments] = useState([]);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [editingMsgIdx, setEditingMsgIdx] = useState(0);

  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);
  const editingMsgRef = useRef(null);

  const remainingPerPost = Math.max(0, editsLimit - editsUsed);
  const canSend = !isSending && !isProcessing;

  // Carregar o content
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await contentService.getBatch(batchId);
        const batch = res.data || res;
        const c = (batch.contents || []).find(x => x.id === contentId);
        if (!mounted) return;
        if (!c) { setError('Conteúdo não encontrado'); setIsLoading(false); return; }
        setContent(c);
        setImageUrl(c.result_url);
        const meta = c.metadata || {};
        const hist = meta.chat_history || [];
        setMessages(hist.length ? hist : [{ role: 'assistant', content: GREETING, timestamp: new Date().toISOString() }]);
        setEditsUsed(meta.edits_used || 0);
        setEditsLimit(meta.edits_limit || 3);
        if ((meta.edit_job || {}).status === 'processing') {
          setIsProcessing(true);
          startPolling();
        }
      } catch (e) {
        if (mounted) setError('Erro ao carregar o conteúdo');
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => { mounted = false; if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, contentId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isProcessing, isSending]);

  // Mensagens rotativas durante a geração
  useEffect(() => {
    if (isProcessing) {
      setEditingMsgIdx(0);
      editingMsgRef.current = setInterval(() => {
        setEditingMsgIdx(prev => (prev + 1) % EDITING_MESSAGES.length);
      }, EDITING_MSG_INTERVAL_MS);
    } else if (editingMsgRef.current) {
      clearInterval(editingMsgRef.current);
      editingMsgRef.current = null;
    }
    return () => { if (editingMsgRef.current) { clearInterval(editingMsgRef.current); editingMsgRef.current = null; } };
  }, [isProcessing]);

  const editingMessage = EDITING_MESSAGES[editingMsgIdx];

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const res = await contentService.getChatEditStatus(batchId, contentId);
        const d = res.data || {};
        const job = d.edit_job || {};
        if (job.status && job.status !== 'processing') {
          stopPolling();
          setIsProcessing(false);
          setImageUrl(d.result_url);
          if (d.chat_history) setMessages(d.chat_history);
          if (typeof d.edits_used === 'number') setEditsUsed(d.edits_used);
          if (typeof d.edits_limit === 'number') setEditsLimit(d.edits_limit);
          if (job.status === 'failed') setError(job.error || 'A edição falhou. Tente novamente.');
        }
      } catch (e) { /* transitório — segue tentando */ }
    }, POLL_INTERVAL_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, contentId, stopPolling]);

  const handleSend = async (overrideMessage) => {
    const message = (overrideMessage ?? input).trim();
    if (!message || !canSend) return;

    setError(null);
    setIsSending(true);
    setInput('');
    // user msg + placeholder streaming da Mia
    setMessages(prev => [
      ...prev,
      { role: 'user', content: message, timestamp: new Date().toISOString() },
      { role: 'assistant', content: '', streaming: true, timestamp: new Date().toISOString() },
    ]);
    const sentAttachments = attachments;
    setAttachments([]);

    const setLastAssistant = (updater) => {
      setMessages(prev => {
        const copy = [...prev];
        for (let i = copy.length - 1; i >= 0; i--) {
          if (copy[i].role === 'assistant') { copy[i] = { ...copy[i], ...updater(copy[i]) }; break; }
        }
        return copy;
      });
    };

    try {
      const token = localStorage.getItem('authToken');
      const base = process.env.REACT_APP_API_URL || '';
      const resp = await fetch(
        `${base}/api/contents/batches/${batchId}/contents/${contentId}/edit-chat/stream`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify({ message, attachments: sentAttachments }),
        },
      );

      if (!resp.ok || !resp.body) {
        let msg = 'Erro ao falar com a Mia';
        try { const j = await resp.json(); msg = j.detail?.message || j.detail || msg; } catch {}
        setMessages(prev => prev.slice(0, -2)); // remove user + placeholder
        setError(msg);
        setIsSending(false);
        return;
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let acc = '';
      let willEdit = false;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith('data:')) continue;
          let ev;
          try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }

          if (ev.type === 'token') {
            acc += ev.delta || '';
            setLastAssistant(() => ({ content: acc, streaming: true }));
          } else if (ev.type === 'tool') {
            // oculto na UI — a Mia decidiu editar; sem token nesse caso
          } else if (ev.type === 'editing') {
            willEdit = true;
          } else if (ev.type === 'done') {
            setLastAssistant(() => ({ content: ev.reply || acc || '…', streaming: false }));
            if (ev.note) {
              setMessages(prev => [...prev, { role: 'assistant', content: ev.note, timestamp: new Date().toISOString() }]);
            }
            if (typeof ev.edits_limit === 'number') setEditsLimit(ev.edits_limit);
            if (ev.editing || willEdit) {
              setIsProcessing(true);
              startPolling();
            }
          } else if (ev.type === 'error') {
            setLastAssistant(() => ({ content: ev.message || 'Ops, deu erro 😕', streaming: false }));
          }
        }
      }
      setIsSending(false);
    } catch (err) {
      setMessages(prev => prev.slice(0, -2));
      setError(err.message || 'Erro de conexão com a Mia');
      setIsSending(false);
    }
  };

  const handleAttachLibrary = (img) => {
    const previewUrl = img.image_url || img.url || null;
    setAttachments(prev => [...prev, { source: 'library', asset_id: img.id, base64: null, previewUrl, caption: img.caption }]);
    setShowLibraryPicker(false);
  };

  const handleDownload = async () => {
    if (!imageUrl) return;
    try {
      const r = await fetch(imageUrl);
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `post_${contentId}.png`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch { window.open(imageUrl, '_blank'); }
  };

  const imageLibrary = brandIdentity?.image_library || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Loader2 className="w-8 h-8 animate-spin" />
          <span>Carregando editor…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <Link to={`/contents/batch/${batchId}`} className="p-2 rounded-lg hover:bg-gray-100 shrink-0">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <MiaAvatar />
            <div className="min-w-0">
              <h1 className="text-base font-bold text-gray-900 truncate flex items-center gap-1.5">
                Mia <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded-full">IA</span>
              </h1>
              <p className="text-xs text-gray-500 truncate">Editora de posts · {content?.metadata?.dimensions || 'square'}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className={`text-xs font-semibold px-2.5 py-1.5 rounded-lg ${remainingPerPost > 0 ? 'text-emerald-700 bg-emerald-50' : 'text-red-600 bg-red-50'}`}>
              {remainingPerPost}/{editsLimit} edições
            </span>
            <button onClick={handleDownload} className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-medium">
              <Download className="w-4 h-4" /> Baixar
            </button>
          </div>
        </div>
      </div>

      {/* Corpo: imagem (esq) + chat (dir) — empilha no mobile */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-6 flex flex-col lg:grid lg:grid-cols-[1fr_400px] gap-4 sm:gap-6">
        {/* Imagem */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-3 sm:p-4 flex flex-col order-1">
          <div className="relative flex-1 flex items-center justify-center bg-gray-100 rounded-xl overflow-hidden min-h-[280px] sm:min-h-[420px]">
            {imageUrl ? (
              <img src={imageUrl} alt="Post" className="max-w-full max-h-[42vh] lg:max-h-[72vh] object-contain rounded-xl" />
            ) : (
              <div className="text-gray-400">Sem imagem</div>
            )}
            {isProcessing && (
              <div className="absolute inset-0 bg-black/55 backdrop-blur-sm flex flex-col items-center justify-center gap-3 text-white px-6 text-center">
                <Loader2 className="w-10 h-10 animate-spin" />
                <span className="text-sm font-medium transition-all duration-300">{editingMessage}</span>
                <span className="text-xs opacity-80">A Mia está caprichando — pode levar ~1 min 🙂</span>
              </div>
            )}
          </div>
        </div>

        {/* Chat */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 flex flex-col order-2 h-[60vh] lg:h-[calc(100vh-9rem)]">
          {/* Mensagens */}
          <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3">
            {messages.map((msg, idx) => (
              msg.role === 'user' ? (
                <div key={idx} className="flex justify-end">
                  {/* balão do usuário = cinza (cor antiga da Mia) */}
                  <div className="max-w-[80%] rounded-2xl rounded-br-md px-3.5 py-2.5 text-sm bg-gray-100 text-gray-800 shadow-sm leading-relaxed">
                    <LightMarkdown text={msg.content} />
                  </div>
                </div>
              ) : (
                <div key={idx} className="flex justify-start items-start gap-2">
                  <MiaAvatar size="w-8 h-8" />
                  <div className="max-w-[80%]">
                    {/* balão da Mia = laranja da plataforma + texto branco */}
                    <div className="rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm bg-orange-500 text-white shadow-sm leading-relaxed min-h-[2.5rem] flex items-center">
                      {msg.content
                        ? (<span><LightMarkdown text={msg.content} />{msg.streaming && <span className="inline-block w-1.5 h-4 bg-white/70 ml-0.5 align-middle animate-pulse" />}</span>)
                        : (<span className="inline-flex items-center gap-1.5 text-white/90"><Loader2 className="w-4 h-4 animate-spin" /> Mia está pensando…</span>)}
                    </div>
                    {msg.image_url && (
                      <img src={msg.image_url} alt="resultado" className="mt-2 rounded-lg w-full max-w-[220px] border border-gray-200" />
                    )}
                    <span className="text-[10px] text-gray-400 ml-1.5">Mia</span>
                  </div>
                </div>
              )
            ))}
            {/* indicador da geração de imagem em background */}
            {isProcessing && (
              <div className="flex justify-start items-start gap-2">
                <MiaAvatar size="w-8 h-8" />
                <div className="rounded-2xl rounded-bl-md px-3.5 py-2.5 text-sm bg-orange-500 text-white shadow-sm flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="transition-all duration-300">{editingMessage}</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Erro */}
          {error && (
            <div className="mx-3 mb-2 flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-3 py-2 text-xs text-red-700">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div className="flex-1">{error}</div>
            </div>
          )}

          {/* Footer */}
          <div className="border-t border-gray-200 p-3">
            {/* chips */}
            <div className="flex flex-wrap gap-1.5 mb-2">
              {QUICK_CHIPS.map(chip => {
                const Icon = chip.icon;
                return (
                  <button key={chip.id} disabled={!canSend || remainingPerPost <= 0}
                    onClick={() => handleSend(chip.prompt)}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 hover:bg-emerald-50 hover:text-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                    <Icon className="w-3 h-3" /> {chip.label}
                  </button>
                );
              })}
            </div>

            {/* anexos selecionados */}
            {attachments.length > 0 && (
              <div className="flex gap-1.5 mb-2">
                {attachments.map((att, idx) => (
                  <div key={idx} style={CHECKER} className="relative w-11 h-11 rounded-lg border border-emerald-300 overflow-hidden flex items-center justify-center text-xs text-emerald-700" title={att.caption || 'anexo'}>
                    {att.previewUrl
                      ? <img src={att.previewUrl} alt="anexo" className="w-full h-full object-contain p-0.5" />
                      : '🖼'}
                    <button onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px]">×</button>
                  </div>
                ))}
              </div>
            )}

            {/* picker da biblioteca */}
            {showLibraryPicker && (
              <div className="mb-2 p-2 border border-emerald-200 rounded-xl bg-emerald-50 max-h-32 overflow-y-auto">
                <div className="text-xs text-emerald-900 mb-1.5 font-semibold">Biblioteca da marca</div>
                <div className="grid grid-cols-5 gap-1.5">
                  {(imageLibrary || []).slice(0, 14).map(img => {
                    const url = img.image_url || img.url;
                    return (
                      <button key={img.id} onClick={() => handleAttachLibrary(img)}
                        style={CHECKER} className="aspect-square rounded-lg overflow-hidden border border-emerald-200 hover:ring-2 hover:ring-emerald-400" title={img.caption}>
                        <img src={url} alt={img.caption || 'imagem'} className="w-full h-full object-contain p-0.5" />
                      </button>
                    );
                  })}
                  {logoUrl && (
                    <button onClick={() => handleAttachLibrary({ id: 'logo', image_url: logoUrl })}
                      style={CHECKER} className="aspect-square rounded-lg overflow-hidden border border-emerald-300 hover:ring-2 hover:ring-emerald-400 flex items-center justify-center" title="Logo da marca">
                      <img src={logoUrl} alt="logo" className="w-full h-full object-contain p-1" />
                    </button>
                  )}
                  {(!imageLibrary || imageLibrary.length === 0) && !logoUrl && (
                    <div className="col-span-5 text-center text-xs text-emerald-700 py-2">Nenhuma imagem na biblioteca.</div>
                  )}
                </div>
              </div>
            )}

            {/* input */}
            <div className="flex items-end gap-2">
              <button onClick={() => setShowLibraryPicker(p => !p)}
                className={`p-2 rounded-lg shrink-0 ${showLibraryPicker ? 'bg-emerald-100 text-emerald-700' : 'hover:bg-gray-100 text-gray-500'}`}
                title="Anexar imagem da biblioteca">
                <ImageIcon className="w-5 h-5" />
              </button>
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder={remainingPerPost <= 0 ? 'Limite de edições deste post atingido' : 'Fala com a Mia…'}
                rows={1}
                disabled={!canSend}
                className="flex-1 resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-amber-300 focus:border-amber-300 disabled:bg-gray-50 disabled:text-gray-400"
              />
              <button onClick={() => handleSend()} disabled={!input.trim() || !canSend}
                className="p-2.5 rounded-xl bg-amber-400 text-gray-900 disabled:bg-gray-200 disabled:text-gray-400 hover:bg-amber-500 transition-colors shrink-0"
                title="Enviar">
                {(isSending || isProcessing) ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GptImageChatEditor;
